import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Loader2 } from 'lucide-react';
import { api, imgUrl, imgSrcSet } from '../lib/api';
import { useCart } from '../context/CartContext';
import { Stars } from './Stars';
import { toast } from 'sonner';

export const CAT_LABELS = {
  all: 'Tout le labo',
  mycelium: 'Mycélium & LC',
  substrat: 'Substrats & Grain',
  kit: 'Kits Prêts-à-Pousser',
  accessoire: 'Accessoires Labo',
};

export const ProductCard = ({ p, index }) => {
  const { add } = useCart();
  const navigate = useNavigate();
  const hasVariants = (p.variants || []).length > 0;
  const goToProduct = () => navigate(`/produit/${p.slug || p.id}`);

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      onClick={goToProduct}
      className="group flex cursor-pointer flex-col items-center text-center"
      data-testid={`product-card-${p.id}`}
    >
      <div className="relative w-full px-2">
        <div className="relative mx-auto aspect-square w-full max-w-[280px] overflow-hidden rounded-full bg-void ring-1 ring-white/10 transition-all duration-500 group-hover:ring-neon/50">
          <img
            src={imgUrl(p.image, 600)}
            srcSet={imgSrcSet(p.image)}
            sizes="(min-width: 1024px) 22vw, 45vw"
            alt={p.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="pointer-events-none absolute inset-0 rounded-full shadow-[inset_0_0_40px_rgba(0,0,0,0.6)]" />
        </div>
        <span className="clip-frame-sm absolute left-3 top-2 bg-amber-400 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-void shadow-lg sm:left-6">
          {p.difficulty || 'Kit'}
        </span>
        {p.stock <= 5 && (
          <span className="clip-frame-sm absolute right-3 top-2 bg-void/85 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-amber-400 backdrop-blur sm:right-6">
            {p.stock > 0 ? `+${p.stock}` : 'Rupture'}
          </span>
        )}
      </div>

      <p className="mt-5 px-2 font-mono text-[11px] uppercase leading-relaxed tracking-[0.25em] text-cyber">
        {CAT_LABELS[p.category] || p.category}
      </p>
      <h3 className="mt-1 px-2 font-display text-lg font-bold leading-tight text-slate-100 transition-colors group-hover:text-neon">
        {p.name}
      </h3>
      {p.latin && <p className="mt-1 font-mono text-[10px] italic tracking-wide text-slate-500">{p.latin}</p>}
      {p.rating?.count > 0 && (
        <span className="mt-2 inline-flex items-center gap-1.5" data-testid={`product-rating-${p.id}`}>
          <Stars value={p.rating.average} size={12} />
          <span className="font-mono text-[10px] text-slate-500">({p.rating.count})</span>
        </span>
      )}

      <div className="mt-4 flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <span className="font-display text-xl font-extrabold text-slate-100 sm:text-2xl" data-testid={`product-price-${p.id}`}>
          {hasVariants && <span className="mr-1 align-middle font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">dès</span>}
          {p.price.toFixed(2).replace('.', ',')} €
        </span>
        {hasVariants ? (
          <button
            onClick={(e) => { e.stopPropagation(); goToProduct(); }}
            data-testid={`add-to-cart-${p.id}`}
            className="rounded-full border border-cyber/40 px-5 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-cyber transition-all hover:bg-cyber hover:text-void"
          >
            Choisir
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); add(p); toast.success(`${p.name} ajouté au panier`); }}
            data-testid={`add-to-cart-${p.id}`}
            className="flex items-center gap-1.5 rounded-full border border-neon/40 px-5 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neon transition-all hover:bg-neon hover:text-void"
          >
            <Plus size={12} /> Ajouter
          </button>
        )}
      </div>
    </motion.article>
  );
};

const Catalog = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const cat = CAT_LABELS[params.get('cat')] ? params.get('cat') : 'all';
  const setCat = (id) => {
    const next = new URLSearchParams(params);
    if (id === 'all') next.delete('cat'); else next.set('cat', id);
    setParams(next, { replace: true });
  };
  const [query, setQuery] = useState(params.get('q') || '');

  useEffect(() => {
    api.get('/products')
      .then((r) => setProducts(r.data))
      .catch(() => toast.error('Impossible de charger le catalogue'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (cat !== 'all') list = list.filter((p) => p.category === cat);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((p) => `${p.name} ${p.latin} ${p.description}`.toLowerCase().includes(q));
    }
    return list;
  }, [products, cat, query]);

  const groups = useMemo(() => {
    if (cat !== 'all') return null;
    return Object.entries(CAT_LABELS)
      .filter(([id]) => id !== 'all')
      .map(([id, label]) => ({ id, label, items: filtered.filter((p) => p.category === id) }))
      .filter((g) => g.items.length > 0);
  }, [filtered, cat]);

  return (
    <section id="catalogue-complet" data-testid="catalog-section" className="relative mx-auto max-w-7xl px-4 pb-24 pt-12 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-neon/80"
        >
          // Filtrer les références
        </motion.h2>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="RECHERCHER UNE SOUCHE…"
          data-testid="catalog-search-input"
          className="clip-frame-sm w-full max-w-xs border border-white/10 bg-panel px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] text-slate-200 placeholder:text-slate-600 focus:border-neon/60 focus:outline-none"
        />
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="catalog-filters">
        {Object.entries(CAT_LABELS).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setCat(id)}
            data-testid={`filter-${id}`}
            className={`clip-frame-sm px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-all ${
              cat === id
                ? 'bg-neon font-bold text-void'
                : 'border border-white/10 text-slate-400 hover:border-neon/40 hover:text-neon'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-24" data-testid="catalog-loading">
          <Loader2 className="animate-spin text-neon" size={32} />
        </div>
      ) : groups ? (
        <div className="mt-10 space-y-16" data-testid="catalog-grid">
          {groups.map((g) => (
            <div key={g.id} data-testid={`catalog-group-${g.id}`}>
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6 }}
                className="mb-6 flex items-baseline gap-4 border-b border-neon/20 pb-3"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon/70">//</span>
                <h3 className="font-display text-xl font-bold tracking-tight text-slate-100">{g.label}</h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{g.items.length} références</span>
              </motion.div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-4">
                <AnimatePresence mode="popLayout">
                  {g.items.map((p, i) => (
                    <ProductCard key={p.id} p={p} index={i} />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-4" data-testid="catalog-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} p={p} index={i} />
            ))}
          </AnimatePresence>
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-500" data-testid="catalog-empty">
          Aucune souche ne correspond à cette recherche
        </p>
      )}

    </section>
  );
};

export default Catalog;
